// 当前工作空间：个人空间或某个战队。决定 Demo 库列表与上传归属。持久化在 localStorage。
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/lib/auth/session";
import type { TeamSummary } from "@/lib/api/types";

export type Space = { kind: "personal" } | { kind: "team"; team: TeamSummary };

interface SpaceCtx {
  space: Space;
  setSpace: (teamId: string | null) => void;
}

const KEY = "cs2c.space";
const Ctx = createContext<SpaceCtx | null>(null);

function readStored(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function SpaceProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const [stored, setStored] = useState<string | null>(readStored);

  const space = useMemo<Space>(() => {
    const teams = me?.teams ?? [];
    const t = stored ? teams.find((x) => x.id === stored) : undefined;
    if (t) return { kind: "team", team: t };
    if (stored === "personal" || teams.length === 0) return { kind: "personal" };
    return { kind: "team", team: teams[0] };
  }, [me?.teams, stored]);

  const setSpace = useCallback((teamId: string | null) => {
    const v = teamId ?? "personal";
    setStored(v);
    try {
      localStorage.setItem(KEY, v);
    } catch {
      /* 隐私模式 */
    }
  }, []);

  return <Ctx value={{ space, setSpace }}>{children}</Ctx>;
}

export function useSpace() {
  const v = use(Ctx);
  if (!v) throw new Error("useSpace 必须在 SpaceProvider 内使用");
  return v;
}

export const spaceName = (s: Space) => (s.kind === "team" ? s.team.name : "个人空间");
